import type { NodeDefinition, OrganizationId, RewardPools } from "../domain/types/index.js";
import { ORG_REWARD_POOL } from "./organizations.js";
import { MAP_NODES } from "./map.js";
import { MAP_NODES_HARD } from "./mapHard.js";
import { MAP_NODES_DEEP } from "./mapDeep.js";
import { MAP_NODES_PEAK } from "./mapPeak.js";
import { MAP_NODES_CHOICE } from "./mapChoice.js";
import { MAP_NODES_LABYRINTH } from "./mapLabyrinth.js";
import { MAP_NODES_WIDE } from "./mapWide.js";
import { MAP_NODES_PRODUCT_RELEASE } from "./mapProductRelease.js";
import { MAP_NODES_STABILIZE } from "./mapStabilize.js";
import { MAP_NODES_FOUNDATION } from "./mapFoundation.js";
import { MAP_NODES_EXPANSION } from "./mapExpansion.js";
import { MAP_NODES_FOCUS } from "./mapFocus.js";
import { MAP_NODES_AUTONOMY } from "./mapAutonomy.js";
import { MAP_NODES_ENDURANCE } from "./mapEndurance.js";
import { MAP_NODES_FORESIGHT } from "./mapForesight.js";
import { MAP_NODES_RECOVERY } from "./mapRecovery.js";
import { MAP_NODES_INHERITANCE } from "./mapInheritance.js";
import { MAP_NODES_GRADUATION } from "./mapGraduation.js";
import { MAP_NODES_WINDOW_SEAT } from "./mapWindowSeat.js";
import { MAP_NODES_INVESTMENT } from "./mapInvestment.js";
import { REWARD_POOLS_FORESIGHT, REWARD_POOLS_GRADUATION, REWARD_POOLS_INHERITANCE, REWARD_POOLS_INVESTMENT, REWARD_POOLS_RECOVERY, REWARD_POOLS_WINDOW_SEAT } from "./rewardPools.js";
import { REWARD_POOLS, REWARD_POOLS_AUTONOMY, REWARD_POOLS_ENDURANCE, REWARD_POOLS_EXPANSION, REWARD_POOLS_FOCUS } from "./rewardPools.js";
import { getRegisteredStage } from "./customRegistry.js";

export const STAGE_IDS = ["standard", "hard", "wide", "deep", "peak", "choice", "labyrinth", "product-release", "stabilize", "foundation", "expansion", "focus", "autonomy", "endurance", "foresight", "recovery", "inheritance", "graduation", "window-seat", "investment"] as const;
export type StageId = (typeof STAGE_IDS)[number];
export const DEFAULT_STAGE_ID: StageId = "standard";

export type StageDefinition = {
  id: string;
  name: string;
  difficulty: string;
  flavor: string;
  nodes: Record<string, NodeDefinition>;
  /** 開始前に持ち込み1枚を選べるステージ（第11期以降の仕組み）。 */
  allowBringIn?: boolean;
  /** 自作ステージの初期山札枚数。未指定なら公式の初期山札規則。 */
  startingDeck?: { intern: number; idle: number; hedge: number };
  /** ステージ専用の報酬プール。未指定なら共通プールを使う。 */
  rewardPools?: RewardPools;
  /** ステージ専用の組織基盤プール。未指定なら共通プールを使う。 */
  orgRewardPool?: readonly OrganizationId[];
};

export const STAGES: Record<StageId, StageDefinition> = {
  standard: {
    id: "standard",
    name: "第1期",
    difficulty: "標準",
    flavor: "立ち上がりの四半期。まだ余裕がある、はずだ。",
    nodes: MAP_NODES,
  },
  hard: {
    id: "hard",
    name: "第2期",
    difficulty: "やや難しい",
    flavor: "プロジェクトが同時に降る。道は交差し、休みは偏っている。",
    nodes: MAP_NODES_HARD,
  },
  wide: {
    id: "wide",
    name: "第3期",
    difficulty: "分岐多め",
    flavor: "同時に走っているプロジェクトから、今やる一本を選ぶ。",
    nodes: MAP_NODES_WIDE,
  },
  deep: {
    id: "deep",
    name: "第4期",
    difficulty: "合流多め",
    flavor: "分かれた道が、また会う。",
    nodes: MAP_NODES_DEEP,
  },
  peak: {
    id: "peak",
    name: "第5期",
    difficulty: "イベント多め",
    flavor: "予定どおりにいかない日ほど、会社の地力が試される。",
    nodes: MAP_NODES_PEAK,
  },
  choice: {
    id: "choice",
    name: "第6期",
    difficulty: "戦略分岐",
    flavor: "遠回りには時間がかかる。近道には、代償がある。",
    nodes: MAP_NODES_CHOICE,
  },
  labyrinth: {
    id: "labyrinth",
    name: "第7期",
    difficulty: "迷宮",
    flavor: "閉鎖される旧棟には、まだ回収すべきものが残っている。",
    nodes: MAP_NODES_LABYRINTH,
  },
  "product-release": {
    id: "product-release",
    name: "第8期",
    difficulty: "長期リリース",
    flavor: "自社プロダクトの公開日。品質、合意、配信を揃えて、顧客へ送り出す。",
    nodes: MAP_NODES_PRODUCT_RELEASE,
  },
  stabilize: {
    id: "stabilize",
    name: "第9期",
    difficulty: "運用定着",
    flavor: "出したあとに残る火を消し、次の四半期へ渡す。",
    nodes: MAP_NODES_STABILIZE,
  },
  foundation: {
    id: "foundation",
    name: "第10期",
    difficulty: "基盤収集",
    flavor: "現場の力を、組織の力に変える四半期。寄り道の先に、基盤がある。",
    nodes: MAP_NODES_FOUNDATION,
  },
  expansion: {
    id: "expansion",
    name: "第11期",
    difficulty: "横展開",
    flavor: "できあがった仕組みを、面に広げる四半期。展開先は、あちこちにある。",
    nodes: MAP_NODES_EXPANSION,
    allowBringIn: true,
    rewardPools: REWARD_POOLS_EXPANSION,
  },
  focus: {
    id: "focus",
    name: "第12期",
    difficulty: "選択と集中",
    flavor: "広げた面を、絞って深くする四半期。やらないことを決める。",
    nodes: MAP_NODES_FOCUS,
    allowBringIn: true,
    rewardPools: REWARD_POOLS_FOCUS,
    orgRewardPool: ["qa", "schedule", "short-focus", "manager-training", "small-elite"],
  },
  autonomy: {
    id: "autonomy",
    name: "第13期",
    difficulty: "連携と自律",
    flavor: "任せ、つなぎ、組織全体の速度を取り戻す四半期。答えは一つではない。",
    nodes: MAP_NODES_AUTONOMY,
    allowBringIn: true,
    rewardPools: REWARD_POOLS_AUTONOMY,
    orgRewardPool: ["qa", "mental", "manager-training", "negotiation"],
  },
  endurance: {
    id: "endurance", name: "第14期", difficulty: "長期持久戦",
    flavor: "続くプロジェクトを、限られた手札と体力で走り切る。止まらず、急ぎすぎず、次へ渡す。",
    nodes: MAP_NODES_ENDURANCE, allowBringIn: true, rewardPools: REWARD_POOLS_ENDURANCE,
    orgRewardPool: ["mental", "schedule", "manager-training", "negotiation"],
  },
  foresight: {
    id: "foresight", name: "第15期", difficulty: "予測と対策",
    flavor: "ハプニングの性質を読み、備えて挑む。相手に合わせて、仕事と進路を切り分ける四半期。",
    nodes: MAP_NODES_FORESIGHT, allowBringIn: true, rewardPools: REWARD_POOLS_FORESIGHT,
    orgRewardPool: ["qa", "schedule", "manager-training", "negotiation"],
  },
  recovery: {
    id: "recovery", name: "第16期", difficulty: "障害と復旧",
    flavor: "止まった仕組み、積み上がる依頼、疲れた現場。復旧方針を選び、通常運用を取り戻す四半期。",
    nodes: MAP_NODES_RECOVERY, allowBringIn: true, rewardPools: REWARD_POOLS_RECOVERY,
    orgRewardPool: ["qa", "mental", "schedule", "negotiation"],
  },
  inheritance: {
    id: "inheritance", name: "第17期", difficulty: "継承と統合",
    flavor: "人に宿った仕事を、次の担い手と仕組みへ渡す四半期。任せる範囲と、支える約束を選ぶ。",
    nodes: MAP_NODES_INHERITANCE, allowBringIn: true, rewardPools: REWARD_POOLS_INHERITANCE,
    orgRewardPool: ["qa", "manager-training", "schedule", "negotiation", "small-elite"],
  },
  graduation: {
    id: "graduation", name: "第18期", difficulty: "採用と標準化",
    flavor: "採用を重ね、増えた人手を組織の出力へ変える四半期。人数だけでは、会社は強くならない。",
    nodes: MAP_NODES_GRADUATION, allowBringIn: true, rewardPools: REWARD_POOLS_GRADUATION,
    orgRewardPool: ["expansion-strategy", "qa", "schedule", "mental", "flat-org"],
  },
  "window-seat": {
    id: "window-seat", name: "第19期", difficulty: "再配置と活用",
    flavor: "空いた席にいるだけでは、誰の力も測れない。役割を見つけ、次の仕事へつなぎ直す。",
    nodes: MAP_NODES_WINDOW_SEAT, allowBringIn: true, rewardPools: REWARD_POOLS_WINDOW_SEAT,
    orgRewardPool: ["mental", "manager-training", "qa", "schedule", "flat-org"],
  },
  investment: {
    id: "investment", name: "第20期", difficulty: "投資と回収",
    flavor: "いまの一手を少し遅くして、次の一手を速くする。回収は、プロジェクトが終わる前に。",
    nodes: MAP_NODES_INVESTMENT, allowBringIn: true, rewardPools: REWARD_POOLS_INVESTMENT,
    orgRewardPool: ["qa", "schedule", "manager-training", "negotiation", "investment-committee"],
  },
};

export function isStageId(value: unknown): value is StageId {
  return typeof value === "string" && (STAGE_IDS as readonly string[]).includes(value);
}

export function getStage(stageId: string | undefined): StageDefinition {
  if (isStageId(stageId)) return STAGES[stageId];
  const custom = getRegisteredStage(stageId);
  if (custom) return custom;
  return STAGES[DEFAULT_STAGE_ID];
}

export function getStageMap(stageId: string | undefined): Record<string, NodeDefinition> {
  return getStage(stageId).nodes;
}

export function getMapNode(stageId: string | undefined, nodeId: string): NodeDefinition | undefined {
  return getStageMap(stageId)[nodeId];
}

export function listStages(): StageDefinition[] {
  return STAGE_IDS.map((id) => STAGES[id]);
}

export function getStageRewardPools(stageId: string | undefined): RewardPools {
  return getStage(stageId).rewardPools ?? REWARD_POOLS;
}

export function resolveOrgPool(stage: StageDefinition): readonly OrganizationId[] {
  return stage.orgRewardPool ?? ORG_REWARD_POOL;
}

export function getStageOrgPool(stageId: string | undefined): readonly OrganizationId[] {
  return resolveOrgPool(getStage(stageId));
}
